/**
 * A faixa de estado da seção das fotografias (E123.F3, GUS-128; textos da GUS-57).
 *
 * Diz em que ponto está a comparação e o que falta, sem erro técnico e sem progresso falso: a
 * classificação e a conferência em andamento são avisos de espera; o material sem fotografia é
 * limitação informativa, não bloqueio (GUS-57, alteração 4); o par sem parecer oferece tentar
 * agora, além da nova tentativa automática.
 */
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { EstadoDasFotografias } from '@/lib/fotografias_do_caso';

const t = ptBR.exameEletronico.fotografias;

const TOM_DO_AVISO = 'border border-warning bg-warning-50 px-sc-3 py-sc-2 text-warning-700';

export interface FotografiasDoCasoEstadoProps {
  estado: EstadoDasFotografias;
  concluido: boolean;
  tentando: boolean;
  onTentarAgora: () => void;
  /**
   * E206.F2 (GUS-296 itens 2 e 5b): o caso aguarda arquivos — nada roda, então nada se diz "em
   * andamento" e "Tentar agora" fica indisponível (a seção diz o que falta).
   */
  aguardando?: boolean;
}

/** "Tentar agora" no par sem parecer: indisponível com o documento concluído ou o caso parado. */
function TentarAgora({
  concluido,
  tentando,
  onTentarAgora,
  aguardando,
}: Omit<FotografiasDoCasoEstadoProps, 'estado'>): JSX.Element {
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-sc-3"
      data-testid="fotografias-estado"
      data-estado="pendente"
    >
      <p className="max-w-prose text-body-sm text-fg-muted">{t.indisponivel}</p>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={concluido || tentando || aguardando === true}
        onClick={onTentarAgora}
        data-testid="fotografias-tentar-agora"
      >
        {tentando ? t.tentando : t.tentarAgora}
      </Button>
    </div>
  );
}

function emEspera(estado: EstadoDasFotografias): boolean {
  return estado === 'organizando' || estado === 'conferindo';
}

export function FotografiasDoCasoEstado({
  estado,
  aguardando = false,
  ...acoes
}: FotografiasDoCasoEstadoProps): JSX.Element | null {
  // E206.F2: com o caso parado, nada roda; o aviso do que falta fica no alto da seção.
  if (aguardando && emEspera(estado)) return null;
  if (emEspera(estado)) {
    return (
      <p
        role="status"
        className="text-body-sm text-fg-muted"
        data-testid="fotografias-estado"
        data-estado={estado}
      >
        {estado === 'organizando' ? t.organizando : t.conferindo}
      </p>
    );
  }
  if (estado === 'selecione' || estado === 'sem_fotos') {
    const texto = estado === 'selecione' ? t.selecione : t.semFotos;
    return (
      <div
        role="status"
        className={TOM_DO_AVISO}
        data-testid="fotografias-estado"
        data-estado={estado}
      >
        <p className="text-body-sm font-semibold">{texto.titulo}</p>
        <p className="mt-1 max-w-prose text-body-sm">{texto.texto}</p>
      </div>
    );
  }
  return estado === 'pendente' ? <TentarAgora {...acoes} aguardando={aguardando} /> : null;
}
